import { defineStore } from 'pinia'
import { Capacitor } from '@capacitor/core'
import { useStoreAuth } from '@/stores/storeAuth'
import { useAppBadge } from '@/composables/useAppBadge'
import { supabase } from '@/supabase'

let storeAuth = null
let realtimeSubscription = null

/**
 * Notification types produced by the backend
 * (docs/17-social-engagement-and-notifications.md)
 */
export const NOTIFICATION_TYPES = {
  // Social
  MESSAGE_RECEIVED: 'message_received',
  COMMENT_RECEIVED: 'comment_received',
  COMMENT_LIKE: 'comment_like',
  REACTION_RECEIVED: 'reaction_received',
  FOLLOW_RECEIVED: 'follow_received',
  // Club-related
  CLUB_JOIN_REQUEST: 'club_join_request',
  CLUB_REQUEST_APPROVED: 'club_request_approved',
  CLUB_REQUEST_REJECTED: 'club_request_rejected',
  CLUB_PROMOTED: 'club_promoted',
  CLUB_DEMOTED: 'club_demoted',
  // Collaborative list-related
  LIST_JOIN_REQUEST: 'list_join_request',
  LIST_REQUEST_APPROVED: 'list_request_approved',
  LIST_REQUEST_REJECTED: 'list_request_rejected',
  LIST_INVITE_RECEIVED: 'list_invite_received',
  LIST_INVITE_ACCEPTED: 'list_invite_accepted',
  LIST_INVITE_DECLINED: 'list_invite_declined',
  LIST_MEMBER_JOINED: 'list_member_joined',
  LIST_MEMBER_LEFT: 'list_member_left'
}

/**
 * Notification channels
 */
export const NOTIFICATION_CHANNELS = {
  IN_APP: 'in_app',
  EMAIL: 'email',
  PUSH: 'push'
}

export const useStoreNotifications = defineStore('storeNotifications', {
  state: () => ({
    // Notifications list
    notifications: [],

    // Loading states
    loading: false,
    loadingMore: false,
    loaded: false,

    // Error state
    error: null,

    // Pagination
    hasMore: false,
    offset: 0,
    limit: 20,

    // Unread count (for badge)
    unreadCount: 0,

    // User preferences
    preferences: {},
    preferencesLoaded: false,

    // Filter
    showUnreadOnly: false
  }),

  getters: {
    /**
     * Get all notifications
     */
    allNotifications: (state) => state.notifications,

    /**
     * Get unread notifications
     */
    unreadNotifications: (state) =>
      state.notifications.filter((n) => !n.read_at),

    /**
     * Check if notifications are loading
     */
    isLoading: (state) => state.loading,

    /**
     * Check if there are more to load
     */
    canLoadMore: (state) => state.hasMore && !state.loadingMore,

    /**
     * Get notifications grouped by date
     */
    notificationsByDate: (state) => {
      const groups = {}
      const today = new Date().toDateString()
      const yesterday = new Date(Date.now() - 86400000).toDateString()

      state.notifications.forEach((notification) => {
        const date = new Date(notification.created_at).toDateString()
        let label = date

        if (date === today) {
          label = 'Today'
        } else if (date === yesterday) {
          label = 'Yesterday'
        }

        if (!groups[label]) groups[label] = []
        groups[label].push(notification)
      })

      return groups
    },

    /**
     * Check if a notification type is enabled for a channel
     */
    isNotificationEnabled: (state) => (channel, type) => {
      const key = `${channel}_${type}`
      // Default to enabled if not explicitly set
      return state.preferences[key] !== false
    }
  },

  actions: {
    /**
     * Initialize the notifications store
     */
    init() {
      storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log(
          '[storeNotifications] Skipping init - user not authenticated'
        )
        return
      }
      this.fetchUnreadCount()
      this.subscribeToRealtime()
    },

    /**
     * Fetch notifications from the database
     * @param {object} options - Fetch options
     * @param {boolean} options.refresh - If true, reset pagination and fetch fresh
     * @param {boolean} options.unreadOnly - If true, only fetch unread notifications
     */
    async fetch(options = {}) {
      const { refresh = true, unreadOnly = this.showUnreadOnly } = options

      if (refresh) {
        this.loading = true
        this.offset = 0
        this.notifications = []
      } else {
        this.loadingMore = true
      }

      this.error = null

      try {
        let query = supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false })
          .range(this.offset, this.offset + this.limit)

        if (unreadOnly) {
          query = query.is('read_at', null)
        }

        const { data, error } = await query

        if (error) throw error

        const items = data || []

        // The range fetches limit+1 rows as a has-more probe; only the
        // kept rows advance the offset
        this.hasMore = items.length === this.limit + 1
        const kept = this.hasMore ? items.slice(0, this.limit) : items

        if (refresh) {
          this.notifications = kept
        } else {
          this.notifications = [...this.notifications, ...kept]
        }

        this.offset += kept.length
        this.loaded = true
      } catch (err) {
        console.error('[storeNotifications] Error fetching notifications:', err)
        this.error = err.message || 'Failed to load notifications'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    /**
     * Fetch just the unread count (for badge)
     */
    async fetchUnreadCount() {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('id', { count: 'exact', head: true })
          .is('read_at', null)

        if (error) throw error

        this.unreadCount = count || 0
        this.syncAppBadge()
      } catch (err) {
        console.error('[storeNotifications] Error fetching unread count:', err)
      }
    },

    /**
     * Reflect the unread count on the iOS app icon badge
     */
    syncAppBadge() {
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
        return
      }
      const { setBadge } = useAppBadge()
      setBadge(this.unreadCount)
    },

    /**
     * Load more notifications (pagination)
     */
    async loadMore() {
      if (!this.hasMore || this.loadingMore) return
      await this.fetch({ refresh: false })
    },

    /**
     * Refresh notifications (pull-to-refresh)
     */
    async refresh() {
      await this.fetch({ refresh: true })
      await this.fetchUnreadCount()
    },

    /**
     * Refresh after app resumes from background
     */
    async refreshAfterResume() {
      console.log('[storeNotifications] Refreshing after app resume')
      this.unsubscribeFromRealtime()
      await this.fetchUnreadCount()
      if (this.loaded) {
        await this.fetch({ refresh: true })
      }
      this.subscribeToRealtime()
    },

    /**
     * Set filter for unread only
     * @param {boolean} unreadOnly
     */
    async setFilter(unreadOnly) {
      this.showUnreadOnly = unreadOnly
      await this.fetch({ refresh: true, unreadOnly })
    },

    /**
     * Mark a single notification as read
     * @param {string} notificationId
     */
    async markAsRead(notificationId) {
      // Find notification
      const notification = this.notifications.find(
        (n) => n.id === notificationId
      )
      if (!notification || notification.read_at) return

      // Optimistic update
      const previousReadAt = notification.read_at
      notification.read_at = new Date().toISOString()
      this.unreadCount = Math.max(0, this.unreadCount - 1)
      this.syncAppBadge()

      try {
        const { error } = await supabase
          .from('notifications')
          .update({ read_at: notification.read_at })
          .eq('id', notificationId)

        if (error) throw error
      } catch (err) {
        console.error('[storeNotifications] Error marking as read:', err)
        // Revert optimistic update
        notification.read_at = previousReadAt
        this.unreadCount++
      }
    },

    /**
     * Mark all notifications as read
     */
    async markAllAsRead() {
      if (this.unreadCount === 0) return

      // Optimistic update
      const previousUnreadCount = this.unreadCount
      const previousNotifications = this.notifications.map((n) => ({
        ...n
      }))
      const now = new Date().toISOString()

      this.notifications.forEach((n) => {
        if (!n.read_at) n.read_at = now
      })
      this.unreadCount = 0
      this.syncAppBadge()

      try {
        const { error } = await supabase
          .from('notifications')
          .update({ read_at: now })
          .is('read_at', null)

        if (error) throw error
      } catch (err) {
        console.error('[storeNotifications] Error marking all as read:', err)
        // Revert optimistic update
        this.notifications = previousNotifications
        this.unreadCount = previousUnreadCount
      }
    },

    /**
     * Delete a notification
     * @param {string} notificationId
     */
    async deleteNotification(notificationId) {
      const index = this.notifications.findIndex((n) => n.id === notificationId)
      if (index === -1) return

      const removed = this.notifications[index]
      const wasUnread = !removed.read_at

      // Optimistic update
      this.notifications.splice(index, 1)
      if (wasUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }

      try {
        const { error } = await supabase
          .from('notifications')
          .delete()
          .eq('id', notificationId)

        if (error) throw error
      } catch (err) {
        console.error('[storeNotifications] Error deleting notification:', err)
        // Revert
        this.notifications.splice(index, 0, removed)
        if (wasUnread) this.unreadCount++
      }
    },

    /**
     * Fetch user's notification preferences
     */
    async fetchPreferences() {
      try {
        const { data, error } = await supabase
          .from('notification_preferences')
          .select('channel, notification_type, enabled')

        if (error) throw error

        // Convert to lookup object
        const prefs = {}
        ;(data || []).forEach((pref) => {
          prefs[`${pref.channel}_${pref.notification_type}`] = pref.enabled
        })

        this.preferences = prefs
        this.preferencesLoaded = true
      } catch (err) {
        console.error('[storeNotifications] Error fetching preferences:', err)
      }
    },

    /**
     * Update a notification preference
     * @param {string} channel - 'in_app', 'email', or 'push'
     * @param {string} type - notification type
     * @param {boolean} enabled
     */
    async updatePreference(channel, type, enabled) {
      const key = `${channel}_${type}`
      const previousValue = this.preferences[key]

      // Optimistic update
      this.preferences[key] = enabled

      try {
        const { error } = await supabase
          .from('notification_preferences')
          .upsert(
            {
              user_id: storeAuth.user.id,
              channel,
              notification_type: type,
              enabled
            },
            {
              onConflict: 'user_id,channel,notification_type'
            }
          )

        if (error) throw error
      } catch (err) {
        console.error('[storeNotifications] Error updating preference:', err)
        // Revert
        this.preferences[key] = previousValue
      }
    },

    /**
     * Subscribe to real-time notification updates
     */
    subscribeToRealtime() {
      if (realtimeSubscription) return

      const userId = storeAuth?.user?.id
      if (!userId) return

      realtimeSubscription = supabase
        .channel('notifications_realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log(
              '[storeNotifications] New notification received:',
              payload
            )
            // Increment unread count
            this.unreadCount++
            this.syncAppBadge()
            // Optionally prepend the new notification
            if (payload.new) {
              this.notifications.unshift(payload.new)
            }
          }
        )
        .subscribe()
    },

    /**
     * Unsubscribe from real-time updates
     */
    unsubscribeFromRealtime() {
      if (realtimeSubscription) {
        supabase.removeChannel(realtimeSubscription)
        realtimeSubscription = null
      }
    },

    /**
     * Clear all notification state
     */
    clear() {
      this.notifications = []
      this.loaded = false
      this.loading = false
      this.error = null
      this.hasMore = false
      this.offset = 0
      this.unreadCount = 0
      this.preferences = {}
      this.preferencesLoaded = false
      this.showUnreadOnly = false
      this.unsubscribeFromRealtime()
    }
  }
})
